import { useEffect, useMemo, useRef } from "react";
import * as d3 from "d3";
import { apiFetch } from "../../api/apiClient";
import { createPortfolioDateFormatter, DEFAULT_TIME_ZONE } from "../../utils/dateTime";

const HEIGHT = 200;

const margin = {
  top: 20,
  right: 28,
  bottom: 20,
  left: 1
};

export default function HistoricalTilt({ portfolioName = "", onTooltip, timeZone = DEFAULT_TIME_ZONE }) {
  const dateFormatter = useMemo(
    () => createPortfolioDateFormatter(timeZone),
    [timeZone],
  );
  const containerRef = useRef(null);
  const svgRef = useRef(null);

  // Keyboard navigation: which point is selected and whether arrow-key driven
  // navigation is currently active. Refs (not state) so the d3 handlers can
  // read/write without forcing React re-renders or redraws.
  const keyboardIndexRef = useRef(0);
  const keyboardModeRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current) return;

    let cancelled = false;
    let observer;
    let removeKeyListeners = null;

    // Mutable handle to the latest draw cycle's selection helpers. Populated
    // at the end of every `draw()` call so the keyboard/focus listeners
    // (which outlive a single draw) can drive the most recent chart instance.
    const handlersRef = { current: null };

    (async () => {
      let data = [];
      if (portfolioName) {
        try {
          const raw = await apiFetch(`/api/graph/portfolio/${encodeURIComponent(portfolioName)}/metrics/48hr/tracker-tilt`);
          const rows = Array.isArray(raw) ? raw : [];
          data = rows
            .map((row) => {
              const tRaw = row.time ?? row.date ?? row.timestamp;
              const time = tRaw != null ? new Date(tRaw) : null;
              if (!time || Number.isNaN(time.getTime())) return null;
              const motor = Number(row.motor ?? row.currentTilt ?? row.value ?? 0);
              const setpoint = Number(row.setpoint ?? row.motor ?? row.currentTilt ?? row.value ?? 0);
              return { time, setpoint, motor };
            })
            .filter(Boolean)
            .sort((a, b) => a.time - b.time);
        } catch {
          data = [];
        }
      }

      if (cancelled || !containerRef.current) return;

      function draw() {
        
        if (cancelled || !containerRef.current || !svgRef.current) return;
        const width = containerRef.current.clientWidth;
        const innerW = width - margin.left - margin.right;
        const innerH = HEIGHT - margin.top - margin.bottom;

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();

        const g = svg.append("g")
          .attr("transform", `translate(${margin.left},${margin.top})`);

        /* =========================
          SCALES
          ========================= */
        // Anchor to returned data when present (mart may lag wall clock); otherwise
        // fall back to yesterday 00:00 through end of today.
        let start;
        let end;
        if (data.length > 0) {
          const minT = d3.min(data, (d) => d.time);
          const maxT = d3.max(data, (d) => d.time);
          start = new Date(minT);
          start.setHours(0, 0, 0, 0);
          end = new Date(maxT);
          end.setHours(0, 0, 0, 0);
          end.setDate(end.getDate() + 1);
          if (end - start < 48 * 60 * 60 * 1000) {
            end = new Date(start);
            end.setDate(start.getDate() + 2);
          }
        } else {
          start = new Date();
          start.setHours(0, 0, 0, 0);
          start.setDate(start.getDate() - 1);
          end = new Date(start);
          end.setDate(start.getDate() + 2);
        }
   
        
        const x = d3.scaleTime()
          .domain([start, end])
          .range([0, innerW]);

        const y = d3.scaleLinear()
          .domain([-60, 60])
          .range([innerH, 0]);

        /* =========================
          GRIDLINES
          ========================= */
        g.append("g")
          .call(
            d3.axisRight(y)
              .tickValues([-60, -30, 0, 30, 60])
              .tickSize(innerW)
              .tickFormat("")
          )
          .selectAll("line")
          .attr("stroke", "#ddd");

        g.selectAll("path").remove();

        /* =========================
          Y AXIS (RIGHT)
          ========================= */
        g.append("g")
          .attr("transform", `translate(${innerW},0)`)
          .call(
            d3.axisRight(y)
              .tickValues([-60, -30, 0, 30, 60])
              .tickFormat(d => `${d}°`)
          )
          .selectAll("text")
          .style("font-weight", "bold");

        /* =========================
          X AXIS (CUSTOM TICKS)
          ========================= */      
        const hours = [
          ...d3.timeHour.every(1).range(start, end),
          end
        ];

        const xAxis = g.append("g")
          .attr("transform", `translate(0,${innerH})`)
          .call(
            d3.axisBottom(x)
              .tickValues(hours)
              .tickFormat("")
          );

        xAxis.selectAll(".tick line")
          .attr("y2", d => {
            const h = dateFormatter.getHour(d);
            if (h % 6 === 0) return -24;
            if (h % 3 === 0) return -12;
            return -6;
          });

        const labelAxis = g.append("g")
          .attr("transform", `translate(0,${innerH})`)
          .call(
            d3.axisBottom(x)
              .tickValues(hours.filter(d => dateFormatter.getHour(d) % 6 === 0))
              .tickFormat(d =>
                dateFormatter.getHour(d) === 0
                  ? dateFormatter.formatAxisDateTime(d)
                  : dateFormatter.formatAxisTime(d)
              )
          );

        labelAxis.selectAll("text")
          .style("font-weight", "bold")
          .attr("text-anchor", (d, i, nodes) =>
            i === 0 ? "start" :
            i === nodes.length - 1 ? "end" :
            "middle"
          )
          .attr("dx", (d, i, nodes) =>
            i === 0 ? "0.4em" :
            i === nodes.length - 1 ? "-0.4em" :
            "0"
          );

        /* =========================
          LINES
          ========================= */
        const line = key =>
          d3.line()
            .x(d => x(d.time))
            .y(d => y(d[key]));

        g.append("path")
          .datum(data)
          .attr("fill", "none")
          .attr("stroke", "green")
          .attr("stroke-width", 2)
          .attr("d", line("motor"));

        g.append("path")
          .datum(data)
          .attr("fill", "none")
          .attr("stroke", "green")
          .attr("stroke-width", 2)
          .attr("stroke-dasharray", "4,4")
          .attr("d", line("setpoint"));

        const crosshair = g.append("line")
          .attr("y1", 0)
          .attr("y2", innerH)
          .attr("stroke", "#666")
          .attr("stroke-dasharray", "3,3")
          .style("opacity", 0)
          .style("pointer-events", "none");

        /* =========================
          HOVER DOTS
          (hidden by default; shown at the active point on each line)
          ========================= */
        const DOT_R = 5;
        const motorDot = g.append("circle")
          .attr("r", DOT_R)
          .attr("fill", "green")
          .style("pointer-events", "none")
          .style("opacity", 0);

        const setpointDot = g.append("circle")
          .attr("r", DOT_R)
          .attr("fill", "none")
          .attr("stroke", "green")
          .attr("stroke-width", 2)
          .style("pointer-events", "none")
          .style("opacity", 0);

        const centers = data.map(d => x(d.time));

        const nearestIndexByPx = px => {
          let best = 0;
          let bestDx = Infinity;
          for (let i = 0; i < centers.length; i++) {
            const dx = Math.abs(centers[i] - px);
            if (dx < bestDx) {
              bestDx = dx;
              best = i;
            }
          }
          return best;
        };

        // Anchor the tooltip to the top of the higher of the two line points
        // (the smaller y value) at the snapped point, rather than following
        // the cursor, for stable hover behavior.
        const showTooltipForDatum = d => {
          const svgEl = svgRef.current;
          if (!svgEl) return;
          const rect = svgEl.getBoundingClientRect();
          const topY = Math.min(y(d.motor), y(d.setpoint));
          const dateStr = dateFormatter.formatAxisDate(d.time);
          const timeStr = dateFormatter.formatAxisTime(d.time);

          onTooltip({
            visible: true,
            x: rect.left + margin.left + x(d.time),
            y: rect.top + margin.top + topY - 12,
            content: (
              <div>
                <strong>{`${dateStr} ${timeStr}`}</strong>
                <br />
                Tracker Motor: {Math.round(d.motor)}°
                <br />
                Tracker Setpoint: {Math.round(d.setpoint)}°
              </div>
            )
          });
        };

        const showSelectionByIndex = idx => {
          if (!centers.length) return;
          const clamped = Math.max(0, Math.min(centers.length - 1, idx));
          keyboardIndexRef.current = clamped;
          const d = data[clamped];
          if (!d) return;

          crosshair
            .attr("x1", centers[clamped])
            .attr("x2", centers[clamped])
            .style("opacity", 1);

          motorDot
            .attr("cx", centers[clamped])
            .attr("cy", y(d.motor))
            .style("opacity", 1);

          setpointDot
            .attr("cx", centers[clamped])
            .attr("cy", y(d.setpoint))
            .style("opacity", 1);

          showTooltipForDatum(d);
        };

        const hideSelection = () => {
          crosshair.style("opacity", 0);
          motorDot.style("opacity", 0);
          setpointDot.style("opacity", 0);
          onTooltip(null);
        };

        // Given a direction (-1/+1) and whether Shift is held, return the next
        // index. Without Shift this is a single granular step; with Shift it
        // jumps to the point ~1 hour away (and at least one step) so the user
        // can skip the dense sub-hourly readings.
        const HOUR_MS = 60 * 60 * 1000;
        const indexForStep = (dir, shift) => {
          const cur = keyboardIndexRef.current;
          if (!shift || !data[cur]) return cur + dir;
          const targetMs = data[cur].time.getTime() + dir * HOUR_MS;
          let best = cur;
          let bestDx = Infinity;
          for (let i = 0; i < data.length; i++) {
            const dx = Math.abs(data[i].time.getTime() - targetMs);
            if (dx < bestDx) {
              bestDx = dx;
              best = i;
            }
          }
          // Guarantee forward progress even if the nearest point rounds back
          // to the current index (e.g. sparse data near the edges).
          if (best === cur) best = cur + dir;
          return best;
        };

        g.append("rect")
          .attr("width", innerW)
          .attr("height", innerH)
          .attr("fill", "transparent")
          .style("cursor", "crosshair")
          .on("pointermove", (event) => {
            if (!centers.length) return;
            // Any real pointer movement on the chart kicks us out of keyboard
            // navigation mode and back into pointer-follow mode.
            keyboardModeRef.current = false;
            const [mx] = d3.pointer(event);
            showSelectionByIndex(nearestIndexByPx(mx));
          })
          .on("pointerleave", () => {
            // If the chart still has keyboard focus when the pointer leaves,
            // fall back to keyboard-navigation mode so the user can keep
            // arrow-keying without losing the visible selection.
            if (svgRef.current && document.activeElement === svgRef.current) {
              keyboardModeRef.current = true;
              showSelectionByIndex(keyboardIndexRef.current);
            } else {
              hideSelection();
            }
          });

        // Publish this draw cycle's selection helpers so the keyboard/focus
        // listeners (which outlive a single draw) can drive the most recent
        // chart instance.
        handlersRef.current = {
          centersLength: centers.length,
          showSelectionByIndex,
          hideSelection,
          indexForStep,
        };

        // If the chart was already focused when this draw ran (e.g. resize
        // fired while the user was navigating with the keyboard), re-render
        // the selection so the crosshair persists across redraws.
        if (
          keyboardModeRef.current &&
          svgRef.current &&
          document.activeElement === svgRef.current
        ) {
          showSelectionByIndex(keyboardIndexRef.current);
        }
      }

      draw();
      observer = new ResizeObserver(draw);
      observer.observe(containerRef.current);

      /* =========================
        KEYBOARD NAVIGATION
        Arrow-key step through points while the <svg> has focus (via Tab or
        click). Listeners read from `handlersRef` so they always drive the
        most recent draw cycle.
        ========================= */
      const svgEl = svgRef.current;

      const handleKeyDown = (e) => {
        const h = handlersRef.current;
        if (!keyboardModeRef.current || !h || !h.centersLength) return;
        // Holding Shift turns each arrow press into a full-hour jump instead
        // of a single granular step. `showSelectionByIndex` clamps to range.
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          h.showSelectionByIndex(h.indexForStep(-1, e.shiftKey));
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          h.showSelectionByIndex(h.indexForStep(1, e.shiftKey));
        }
      };

      const handleFocus = () => {
        const h = handlersRef.current;
        if (!h || !h.centersLength) return;
        keyboardModeRef.current = true;
        h.showSelectionByIndex(keyboardIndexRef.current);
      };

      const handleBlur = () => {
        keyboardModeRef.current = false;
        handlersRef.current?.hideSelection();
      };

      svgEl?.addEventListener("keydown", handleKeyDown);
      svgEl?.addEventListener("focus", handleFocus);
      svgEl?.addEventListener("blur", handleBlur);

      removeKeyListeners = () => {
        svgEl?.removeEventListener("keydown", handleKeyDown);
        svgEl?.removeEventListener("focus", handleFocus);
        svgEl?.removeEventListener("blur", handleBlur);
      };
    })();

    return () => {
      cancelled = true;
      observer?.disconnect();
      removeKeyListeners?.();
    };
  }, [onTooltip, portfolioName, dateFormatter]);

  return (
    <div
      ref={containerRef}
      className="tilt mt-3"
      style={{ width: "100%", height: "auto", minWidth: 0 }}>
      <div className="header">
        <span><strong>TRACKER MOTORS</strong></span>
      </div>
      <svg
        ref={svgRef}
        tabIndex={0}
        role="img"
        aria-label="Tracker tilt chart. Use left and right arrow keys to navigate between points."
        style={{ width: "100%", height: HEIGHT, display: "block" }}
      />
      <div className="legend">
        <span className="tracker">
          <span></span>
          <strong>TRACKER MOTOR</strong>
        </span>
        <span className="setpoint">
          <span></span>
          <strong>TRACKER SETPOINT</strong>
        </span>
      </div>
    </div>
  );
}